/**
 * Auth API surface for the kiosk.
 */

import { ApiClient } from "./client";
import { getAccessToken, refreshSession } from "$lib/stores/auth";

export type AuthState = {
  device_trusted: boolean;
  device_label: string | null;
  telegram_configured: boolean;
};

export type MasterStartResponse = {
  device_uuid: string;
  expires_in_seconds: number;
};

export type MasterVerifyResponse = {
  device_id: number;
  device_label: string;
};

export type Zone = "produccion" | "logistica" | "admin" | "calidad";

export type Me = {
  user_id: number;
  odoo_employee_id: number;
  name: string;
  email: string | null;
  zone: Zone | null;
  role: string;
  // Full list of FilAr role codes (lowercased). `role` stays the primary for
  // compat; authorization/UX should check this list. Older sessions may not
  // carry it — callers fall back to `[role]` (see `userRoles`).
  roles: string[];
  totp_enabled: boolean;
  device: { id: number; label: string } | null;
};

/** Roles held by a session user, falling back to `[role]` for older sessions. */
export function userRoles(user: Me): string[] {
  return user.roles && user.roles.length > 0 ? user.roles : [user.role];
}

/** True when the user holds ANY of the given role codes (case-insensitive). */
export function userHasAnyRole(user: Me, ...allowed: string[]): boolean {
  const want = new Set(allowed.map((r) => r.toLowerCase()));
  return userRoles(user).some((r) => want.has(r.toLowerCase()));
}

export type Employee = {
  id: number;
  name: string;
  primary_role: string | null;
  roles: string[];
  avatar_url: string | null;
};

export type EmployeeListResponse = {
  employees: Employee[];
  stale: boolean;
  as_of: string | null;
};

export type ZoneOption = { zone: Zone; label: string };

export type TotpProfileAction = "enroll" | "confirm" | "disable";

export type TotpProfileResult = {
  secret?: string | null;
  provisioning_uri?: string | null;
};

export type LoginResult =
  | (TokenPair & { kind: "token" })
  | { kind: "zone_options"; zone_options: ZoneOption[]; pending_zone_token: string }
  | { kind: "totp_required"; challenge: string };

export type TokenPair = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  zone: Zone | null;
  user: Me;
};

const client = new ApiClient({
  baseUrl: "",
  tokens: {
    getAccessToken,
    refresh: refreshSession,
  },
});

function classify(body: any): LoginResult {
  if (body && body.access_token) return { kind: "token", ...body };
  if (body && body.zone_options) return { kind: "zone_options", ...body };
  if (body && body.totp_required) return { kind: "totp_required", challenge: body.challenge };
  throw new Error("Unexpected login response shape");
}

export const auth = {
  state(): Promise<AuthState> {
    return client.get<AuthState>("/api/v1/auth/state", { anonymous: true });
  },

  masterStart(masterKey: string): Promise<MasterStartResponse> {
    return client.post<MasterStartResponse>("/api/v1/auth/master/start", {
      body: { master_key: masterKey },
      anonymous: true,
    });
  },

  masterVerify(args: {
    deviceUuid: string;
    code: string;
    deviceLabel: string;
  }): Promise<MasterVerifyResponse> {
    return client.post<MasterVerifyResponse>("/api/v1/auth/master/verify", {
      body: {
        device_uuid: args.deviceUuid,
        code: args.code,
        device_label: args.deviceLabel,
      },
      anonymous: true,
    });
  },

  employees(): Promise<EmployeeListResponse> {
    return client.get<EmployeeListResponse>("/api/v1/auth/employees", {
      anonymous: true,
    });
  },

  async loginByPin(employeeId: number, pin: string): Promise<LoginResult> {
    const body = await client.post<any>("/api/v1/auth/pin", {
      body: { employee_id: employeeId, pin },
      anonymous: true,
    });
    return classify(body);
  },

  async loginByPassword(email: string, password: string): Promise<LoginResult> {
    const body = await client.post<any>("/api/v1/auth/login", {
      body: { email, password },
      anonymous: true,
    });
    return classify(body);
  },

  // A67: challenge de TOTP del login (cuenta con 2FA habilitado). El backend
  // devuelve un TokenPair (o zone_options, igual que pin/password) contra el
  // challenge emitido en el paso anterior — reusa `classify`.
  async verifyTotp(challenge: string, code: string): Promise<LoginResult> {
    const body = await client.post<any>("/api/v1/auth/totp", {
      body: { challenge, code },
      anonymous: true,
    });
    return classify(body);
  },

  me(): Promise<Me> {
    return client.get<Me>("/api/v1/auth/me");
  },

  async logout(reason: "user" | "user_switch" = "user"): Promise<void> {
    await client.post<void>("/api/v1/auth/logout", { body: { reason } });
  },

  // US5: self-service 2FA (enroll → confirm → disable). `enroll` genera un
  // secreto nuevo (guardado deshabilitado en el backend hasta `confirm`).
  totpEnroll(): Promise<TotpProfileResult> {
    return client.post<TotpProfileResult>("/api/v1/auth/profile/totp", {
      body: { action: "enroll" },
    });
  },

  totpConfirm(code: string): Promise<TotpProfileResult> {
    return client.post<TotpProfileResult>("/api/v1/auth/profile/totp", {
      body: { action: "confirm", code },
    });
  },

  totpDisable(code: string): Promise<TotpProfileResult> {
    return client.post<TotpProfileResult>("/api/v1/auth/profile/totp", {
      body: { action: "disable", code },
    });
  },
};

// `chooseZone` needs a custom header. The ApiClient is intentionally simple;
// we send this one directly.
export async function chooseZoneWithToken(
  zone: Zone,
  pendingZoneToken: string
): Promise<TokenPair> {
  const resp = await fetch("/api/v1/auth/zone", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Pending-Zone-Token": pendingZoneToken,
    },
    credentials: "include",
    body: JSON.stringify({ zone }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: "unknown_error", message: resp.statusText }));
    throw new Error(err.message ?? `HTTP ${resp.status}`);
  }
  return (await resp.json()) as TokenPair;
}
